const API_CACHE_PREFIX = 'ysm:api-cache:'
const API_CACHE_INDEX_KEY = `${API_CACHE_PREFIX}index`

/** 同一页面内短时间重复请求直接复用，避免连打 */
export const DEFAULT_API_CACHE_TTL = 5 * 1000
export const STATIC_API_CACHE_TTL = 15 * 1000
export const VOLATILE_API_CACHE_TTL = 2 * 1000

/** 网络失败时允许回退的最长时间，过期后不再拿旧数据充数 */
export const DEFAULT_API_STALE_TTL = 2 * 60 * 1000
export const STATIC_API_STALE_TTL = 10 * 60 * 1000
export const VOLATILE_API_STALE_TTL = 30 * 1000

/** 配置类 / 公开列表 */
export const STATIC_CACHE_PATTERNS = [
  /\/api\/v1\/webViewConfig\/?$/,
  /\/api\/v1\/notification\/?$/,
  /\/api\/v1\/lottery\/prizes\/?$/,
  /\/api\/v1\/mall\/products\/?$/,
]

/** 账户 / 余额 / 订单 / 状态 */
export const VOLATILE_CACHE_PATTERNS = [
  /\/user\/usersProfile\/?$/,
  /\/user\/check-in\/?$/,
  /\/user\/fundFlowList\/?$/,
  /\/user\/bankCard\/?$/,
  /\/user\/withdraw\/?$/,
  /\/user\/addresses\/?$/,
  /\/user\/referral\/reward\/list\/?$/,
  /\/user\/detail\/[^/]+\/getPromotions\/?$/,
  /\/api\/v1\/mall\/orders\/?$/,
  /\/api\/v1\/lottery\/records\/?$/,
]

/** 有副作用或不该缓存的 GET */
export const NO_CACHE_PATTERNS = [
  /\/api\/v1\/app-download-log\/?$/,
]

let cacheGeneration = 0
// 每次打开页面使用新序号，旧会话里的缓存不能再当作新鲜数据
let freshEpoch = Date.now()

function getCacheStorage() {
  try {
    return sessionStorage
  } catch (e) {
    return null
  }
}

function stableStringify(value) {
  if (value == null) return ''
  if (typeof value !== 'object') return String(value)
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(',')}]`
  }
  const keys = Object.keys(value).sort()
  return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`
}

function hashString(input) {
  const str = String(input || '')
  let hash = 0
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i)
    hash |= 0
  }
  return `h${Math.abs(hash)}`
}

function readIndex(storage) {
  try {
    const raw = storage.getItem(API_CACHE_INDEX_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

function writeIndex(storage, keys) {
  try {
    storage.setItem(API_CACHE_INDEX_KEY, JSON.stringify(keys))
  } catch (e) {
    // ignore quota errors
  }
}

function trackCacheKey(storage, cacheKey) {
  const index = readIndex(storage)
  if (!index.includes(cacheKey)) {
    index.push(cacheKey)
    writeIndex(storage, index)
  }
}

function untrackCacheKey(storage, cacheKey) {
  const index = readIndex(storage).filter((key) => key !== cacheKey)
  writeIndex(storage, index)
}

export function getCacheGeneration() {
  return cacheGeneration
}

/** 路由切换或回到前台时调用：旧缓存立刻失效，下次请求走网络 */
export function markApiCacheStale() {
  freshEpoch += 1
}

export function getRequestUrl(config) {
  const raw = config.url || ''
  try {
    if (/^https?:\/\//i.test(raw)) {
      return new URL(raw).pathname
    }
    const base = config.baseURL || ''
    if (base && /^https?:\/\//i.test(base)) {
      return new URL(raw, base).pathname
    }
  } catch (e) {
    // fall through
  }
  return raw.split('?')[0]
}

export function isCacheableRequest(config) {
  if (!config || config.skipApiCache || config.force) return false
  const method = (config.method || 'get').toLowerCase()
  if (method !== 'get') return false
  const url = getRequestUrl(config)
  return !NO_CACHE_PATTERNS.some((pattern) => pattern.test(url))
}

export function isCacheablePayload(data) {
  if (data == null || typeof data !== 'object') return true
  if (!Object.prototype.hasOwnProperty.call(data, 'code')) return true
  return data.code === 0
}

export function getCachePolicy(config) {
  if (typeof config.cacheTTL === 'number') {
    const ttl = config.cacheTTL
    return {
      fresh: ttl > 0 ? Math.min(ttl, DEFAULT_API_CACHE_TTL) : 0,
      stale: ttl > 0 ? ttl : 0,
    }
  }

  const url = getRequestUrl(config)
  if (STATIC_CACHE_PATTERNS.some((pattern) => pattern.test(url))) {
    return { fresh: STATIC_API_CACHE_TTL, stale: STATIC_API_STALE_TTL }
  }
  if (VOLATILE_CACHE_PATTERNS.some((pattern) => pattern.test(url))) {
    return { fresh: VOLATILE_API_CACHE_TTL, stale: VOLATILE_API_STALE_TTL }
  }
  return { fresh: DEFAULT_API_CACHE_TTL, stale: DEFAULT_API_STALE_TTL }
}

export function getCacheTTL(config) {
  return getCachePolicy(config).fresh
}

export function getCacheKey(config, token) {
  if (config.cacheKey) {
    return `${API_CACHE_PREFIX}${config.cacheKey}`
  }

  const baseURL = config.baseURL || process.env.VUE_APP_BASE_API || ''
  const url = getRequestUrl(config)
  const params = stableStringify(config.params || {})
  const scope = token ? `u:${hashString(token)}` : 'public'

  return `${API_CACHE_PREFIX}${scope}:${baseURL}:${url}:${params}`
}

export function readApiCache(cacheKey, allowStale = false) {
  const storage = getCacheStorage()
  if (!storage || !cacheKey) return null

  try {
    const raw = storage.getItem(cacheKey)
    if (raw == null) return null

    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || !('data' in parsed)) {
      return null
    }

    const now = Date.now()
    const freshUntil = typeof parsed.freshUntil === 'number' ? parsed.freshUntil : 0
    const staleUntil = typeof parsed.staleUntil === 'number'
      ? parsed.staleUntil
      : (typeof parsed.expiresAt === 'number' ? parsed.expiresAt : 0)

    const isFresh = parsed.epoch === freshEpoch && freshUntil > now
    if (!allowStale) {
      return isFresh ? parsed.data : null
    }
    if (staleUntil <= now) return null
    return parsed.data
  } catch (e) {
    return null
  }
}

export function writeApiCache(cacheKey, data, freshTtl, staleTtl = freshTtl) {
  const storage = getCacheStorage()
  if (!storage || !cacheKey || typeof freshTtl !== 'number' || freshTtl <= 0) return
  if (!isCacheablePayload(data)) return

  const now = Date.now()
  const entry = {
    data,
    epoch: freshEpoch,
    cachedAt: now,
    freshUntil: now + freshTtl,
    staleUntil: now + (typeof staleTtl === 'number' && staleTtl > 0 ? staleTtl : freshTtl),
  }

  try {
    storage.setItem(cacheKey, JSON.stringify(entry))
    trackCacheKey(storage, cacheKey)
  } catch (e) {
    try {
      const index = readIndex(storage)
      index.slice(0, Math.min(5, index.length)).forEach((key) => {
        storage.removeItem(key)
        untrackCacheKey(storage, key)
      })
      storage.setItem(cacheKey, JSON.stringify(entry))
      trackCacheKey(storage, cacheKey)
    } catch (err) {
      // ignore
    }
  }
}

export function clearApiCache() {
  cacheGeneration += 1
  const storage = getCacheStorage()
  if (!storage) return

  try {
    const index = readIndex(storage)
    index.forEach((key) => storage.removeItem(key))
    storage.removeItem(API_CACHE_INDEX_KEY)

    const keysToRemove = []
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i)
      if (key && key.startsWith(API_CACHE_PREFIX)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => storage.removeItem(key))
  } catch (e) {
    // ignore
  }
}
